import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JobService from '../services/JobService';
import UserService from '../services/UserService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { addDays, fmtDayLong, fmtTimeRange, toIsoDate } from '../utils/format';
import { navigationUrl } from '../utils/geo';
import { useCompanyLogo } from '../components/common/companyLogo';

const PRINT_CSS = `
@page { size: A4; margin: 14mm; }
@media print {
  .no-print { display: none !important; }
  html, body { background: #fff !important; }
  .sheet { box-shadow: none !important; margin: 0 !important; padding: 0 !important; width: auto !important; min-height: 0 !important; }
  .person { break-inside: avoid-page; }
  .job { break-inside: avoid; }
}
`;

const UNASSIGNED = 'none';

/** Timed jobs by start time, then the ones without a time. */
const byTime = (a, b) => (a.scheduledStart || '99').localeCompare(b.scheduledStart || '99') || a.id - b.id;

/**
 * The day's field work per person, to print or share (WhatsApp, Viber…): time, job, client with phone, KO/parcel,
 * owner and a navigation link. A job with a crew of two is listed under both people.
 */
export default function DayPlanPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { user } = useAuth();
    const logo = useCompanyLogo();
    const [params, setParams] = useSearchParams();
    const date = params.get('date') || toIsoDate(new Date());
    const person = params.get('person') || '';
    const [jobs, setJobs] = useState(null);
    const [people, setPeople] = useState([]);

    useEffect(() => { UserService.directory().then(setPeople).catch(() => {}); }, []);
    useEffect(() => {
        setJobs(null);
        JobService.planner(date, date, person || undefined)
            .then(d => setJobs(d.scheduled))
            .catch(() => { setJobs([]); showToast(t('common.loadError'), 'error'); });
    }, [date, person, showToast, t]);

    const set = (patch) => setParams(Object.fromEntries(Object.entries({ date, person, ...patch }).filter(([, v]) => v)));
    const day = new Date(date + 'T00:00');
    const title = `${t('dayPlan.title')} · ${fmtDayLong(day)}`;

    const groups = useMemo(() => {
        if (!jobs) return [];
        const map = new Map();
        const add = (key, name, job) => {
            if (!map.has(key)) map.set(key, { key, name, jobs: [] });
            map.get(key).jobs.push(job);
        };
        jobs.forEach(j => {
            const crew = person ? (j.assignees || []).filter(a => String(a.id) === person) : (j.assignees || []);
            if (!crew.length) add(UNASSIGNED, t('dayPlan.unassigned'), j);
            crew.forEach(a => add(String(a.id), `${a.firstName} ${a.lastName}`, j));
        });
        return [...map.values()]
            .map(g => ({ ...g, jobs: [...g.jobs].sort(byTime) }))
            .sort((a, b) => (a.key === UNASSIGNED) - (b.key === UNASSIGNED) || a.name.localeCompare(b.name));
    }, [jobs, person, t]);

    const place = (j) => [j.cadastralZone && `${t('jobs.fields.cadastralZone')} ${j.cadastralZone}`, j.parcelNumber && `${t('jobs.parcelShort')} ${j.parcelNumber}`]
        .filter(Boolean).join(', ');

    const shareText = () => [
        title,
        ...groups.flatMap(g => ['', `— ${g.name} —`, ...g.jobs.flatMap(j => [
            `${fmtTimeRange(j) || t('planner.noTime')} · ${[j.number, j.title].filter(Boolean).join(' ')}`,
            `${t('jobs.fields.client')}: ${j.client?.name}${j.client?.phone ? `, ${j.client.phone}` : ''}`,
            place(j),
            j.latitude != null ? `${t('location.navigate')}: ${navigationUrl(j.latitude, j.longitude)}` : '',
            '',
        ].filter((line, i, all) => line || all[i - 1]))]),
    ].join('\n').replace(/\n{3,}/g, '\n\n').trim();

    const share = async () => {
        const text = shareText();
        try {
            if (navigator.share) { await navigator.share({ title, text }); return; }
            await navigator.clipboard.writeText(text);
            showToast(t('dayPlan.copied'), 'success');
        } catch (e) {
            if (e?.name !== 'AbortError') showToast(t('common.error'), 'error');
        }
    };

    return (
        <Box sx={{ bgcolor: '#E5E7EB', minHeight: '100vh', py: 3, px: { xs: 1, sm: 3 } }}>
            <style>{PRINT_CSS}</style>
            <Box className="no-print" sx={{ maxWidth: 960, mx: 'auto', mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/planner')} sx={{ color: '#334155' }}>{t('planner.title')}</Button>
                <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#fff', borderRadius: '10px', px: 0.5 }}>
                    <IconButton size="small" onClick={() => set({ date: toIsoDate(addDays(day, -1)) })} aria-label={t('dayPlan.prevDay')}><ChevronLeftIcon /></IconButton>
                    <TextField type="date" size="small" variant="standard" value={date} onChange={(e) => e.target.value && set({ date: e.target.value })}
                               slotProps={{ input: { disableUnderline: true } }} sx={{ width: 140 }} />
                    <IconButton size="small" onClick={() => set({ date: toIsoDate(addDays(day, 1)) })} aria-label={t('dayPlan.nextDay')}><ChevronRightIcon /></IconButton>
                </Box>
                <Button size="small" variant="outlined" sx={{ bgcolor: '#fff' }} onClick={() => set({ date: toIsoDate(new Date()) })}>{t('planner.today')}</Button>
                <Button size="small" variant="outlined" sx={{ bgcolor: '#fff' }} onClick={() => set({ date: toIsoDate(addDays(new Date(), 1)) })}>{t('planner.tomorrow')}</Button>
                <TextField select size="small" value={person} onChange={(e) => set({ person: e.target.value })}
                           sx={{ minWidth: 170, bgcolor: '#fff' }} slotProps={{ select: { displayEmpty: true } }}>
                    <MenuItem value="">{t('planner.everyone')}</MenuItem>
                    {people.map(p => <MenuItem key={p.id} value={String(p.id)}>{p.id === user?.id ? t('planner.mine') : `${p.firstName} ${p.lastName}`}</MenuItem>)}
                </TextField>
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" startIcon={<ShareOutlinedIcon />} sx={{ bgcolor: '#fff' }} onClick={share} disabled={!groups.length}>{t('dayPlan.share')}</Button>
                <Button variant="contained" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()} disabled={!groups.length}>{t('dayPlan.print')}</Button>
            </Box>

            <Box className="sheet" sx={{
                bgcolor: '#fff', width: '210mm', maxWidth: '100%', minHeight: { sm: '297mm' }, mx: 'auto', p: { xs: 2.5, sm: '14mm' },
                boxShadow: '0 4px 24px rgba(0,0,0,0.12)', fontFamily: 'Montserrat, Arial, sans-serif', color: '#111827', boxSizing: 'border-box',
            }}>
                <Box sx={{ borderBottom: '2px solid #427898', pb: 1.25, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                    {logo && <Box component="img" src={logo} alt="" sx={{ maxWidth: 110, maxHeight: 52, objectFit: 'contain', flexShrink: 0 }} />}
                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#427898' }}>{user?.companyName}</Typography>
                        <Typography sx={{ fontSize: 20, fontWeight: 800 }}>{title}</Typography>
                    </Box>
                </Box>
                {jobs === null ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
                ) : !groups.length ? (
                    <Typography sx={{ color: '#6B7280', textAlign: 'center', py: 6 }}>{t('dayPlan.empty')}</Typography>
                ) : groups.map(g => (
                    <Box key={g.key} className="person" sx={{ mb: 3.5 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#111827', bgcolor: '#F1F5F9', px: 1.25, py: 0.75, borderRadius: '6px', mb: 1 }}>
                            {g.name} <Box component="span" sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>· {t('dayPlan.jobsCount', { count: g.jobs.length })}</Box>
                        </Typography>
                        {g.jobs.map(j => (
                            <Box key={j.id} className="job" sx={{ display: 'flex', gap: 2, py: 1.25, px: 1.25, borderBottom: '1px solid #E5E7EB' }}>
                                <Box sx={{ width: 92, flexShrink: 0, fontSize: 14, fontWeight: 800, color: j.scheduledStart ? '#111827' : '#9CA3AF' }}>
                                    {fmtTimeRange(j) || t('planner.noTime')}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0, fontSize: 13, color: '#374151', lineHeight: 1.55 }}>
                                    <Box sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                                        {[j.number, j.title].filter(Boolean).join(' · ')}
                                        {/* Automatic names already start with the category. */}
                                        {j.category && !j.title?.includes(j.category.name) && <Box component="span" sx={{ fontWeight: 500, color: '#6B7280' }}> · {j.category.name}</Box>}
                                    </Box>
                                    <div>{t('jobs.fields.client')}: <b>{j.client?.name}</b>{j.client?.phone && <> · <a href={`tel:${j.client.phone}`} style={{ color: 'inherit' }}>{j.client.phone}</a></>}</div>
                                    {(place(j) || j.ownerName) && <div>{[place(j), j.ownerName && `${t('jobs.fields.ownerName')}: ${j.ownerName}`].filter(Boolean).join(' · ')}</div>}
                                    {j.latitude != null && (
                                        <div>
                                            <a className="no-print" href={navigationUrl(j.latitude, j.longitude)} target="_blank" rel="noopener noreferrer" style={{ color: '#427898', fontWeight: 700 }}>
                                                {t('location.navigate')}
                                            </a>
                                            <span style={{ color: '#6B7280', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>
                                                {' '}GPS {j.latitude.toFixed(5)}, {j.longitude.toFixed(5)}
                                            </span>
                                        </div>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
